import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import IGlassArea from 'components/interfaces/IGlassArea';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { setWallMetaData } from 'store/wallsMetaData/actions';

import {
  setPreviousWall,
  setWallMetaData as setSelectedWallMetaData,
} from 'store/selectedWall/actions';

import copyWall from '../propertiesBar/services/copyWall';
import doWallPreSaveActions from './src/glassWall/doWallPreSaveActions';
import getAmountOfGlassAreas from '../services/getAmountOfGlassAreas';
import getDefaultGlassArea from './src/glassWall/glassAreas/getDefaultGlassArea';
import getDefaultGlassAreaMetaData from '../services/getDefaultGlassAreaMetaData';
import getDoorsFromWallParts from '../services/getDoorsFromWallParts';
import DraggableModal from './src/DraggableModal';
import IValidationError from './src/glassWall/IValidationError';
import placeGlasses from './src/glassWall/placeGlasses';
import Tabs from './src/glassWall/Tabs';
import tabTypes from './src/glassWall/tabTypes';
import validateGlassWall from './src/glassWall/validateGlassWall';

import 'style/layout/Forms.css';

const GlassWallModal = ({
  closeModalNotSaved,
  glassAreas,
  hasWritePermissions,
  restartFields,
  setGlassAreas,
  wallCoordinates,
}: {
  closeModalNotSaved: () => void;
  glassAreas: IGlassArea[];
  hasWritePermissions: boolean;
  restartFields: () => void;
  setGlassAreas: (areas: IGlassArea[]) => void;
  wallCoordinates: any;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const wall = useSelector((state: any) => state.selectedWall.properties);
  const wallMetaData = useSelector((state: any) => state.selectedWall.metaData);
  const { t } = useTranslation([translations.common]);

  const [selectedTab, setSelectedTab] = useState(tabTypes.wall);
  const [validationErrors, setValidationErrors] = useState<IValidationError[]>(
    []
  );

  const [doors, setDoors] = useState<IGlassWallDoor[]>([]);

  useEffect(() => {
    setValidationErrors(validateGlassWall(wall, wallMetaData, glassAreas));
  }, [wall, glassAreas, wallMetaData]);

  useEffect(() => {
    setDoors(getDoorsFromWallParts(wall));
  }, [wall.parts]);

  useEffect(() => {
    if (doors.length >= glassAreas.length) {
      const newGlassAreas = [...glassAreas];
      while (doors.length >= newGlassAreas.length) {
        newGlassAreas.push(getDefaultGlassArea());
      }

      setGlassAreas(newGlassAreas);
    }

    if (doors.length >= wallMetaData.glassAreas.length) {
      const newWallMetaDataGlassAreas = [...wallMetaData.glassAreas];

      while (doors.length >= newWallMetaDataGlassAreas.length) {
        newWallMetaDataGlassAreas.push(getDefaultGlassAreaMetaData());
      }

      dispatch(
        setSelectedWallMetaData({
          ...wallMetaData,
          glassAreas: newWallMetaDataGlassAreas,
        })
      );
    }
  }, [doors]);

  const editGlass = () => {
    if (validationErrors.length > 0) {
      dispatch(addMessage(`Saving failed!\n${validationErrors[0].message}`));

      return;
    }

    const amountOfGlassAreas = getAmountOfGlassAreas(
      getDoorsFromWallParts(wall),
      wall
    );

    dispatch(
      setWallMetaData({
        ...wallMetaData,
        glassAreas: wallMetaData.glassAreas.slice(0, amountOfGlassAreas),
      })
    );

    dispatch(setPreviousWall(copyWall(wall)));

    const parts = placeGlasses(wall, glassAreas);
    window.UpdateGlassWall(doWallPreSaveActions(wall), parts);

    restartFields();
  };

  return (
    <DraggableModal
      closeModalNotSaved={closeModalNotSaved}
      wallCoordinates={wallCoordinates}
    >
      <Tabs
        doors={doors}
        glassAreas={glassAreas}
        selectedTab={selectedTab}
        setGlassAreas={setGlassAreas}
        setSelectedTab={setSelectedTab}
        validationErrors={validationErrors}
        hasWritePermissions={hasWritePermissions}
        wall={wall}
      />
      <Row>
        <Col>
          <PrimaryButton onClick={editGlass} className="button-edit-popup">
            {t(
              `${translations.common}:Wall Actions.Edit Wall`
            ).toLocaleUpperCase()}
          </PrimaryButton>
        </Col>
      </Row>
    </DraggableModal>
  );
};

export default GlassWallModal;
